/*
 * APIs are powered by PowerLego
 * Website: https://www.powerlego.com/
 */

import { API_URL, ACC_KEY, SIDEBYSIDE_API_URL } from '../config'
import { requestJson } from './request'

/*
 * Address Validator
 * Use cases:        https://www.powerlego.com/cases/address_validator
 * Postman examples: https://documenter.getpostman.com/view/4831254/SzKYPH3c#ca1bdf1f-2318-4fed-aaeb-88708d7c34eb
 */
export const fetchAddress = async (val) => {
    const string = val.toString().replace(/#/, '')

    const myHeaders = new Headers()
    myHeaders.append('Authorization', `Bearer ${ACC_KEY}`)

    const formdata = new FormData()
    formdata.append('term', string)

    const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: formdata,
        redirect: 'follow',
    }

    const url = `${API_URL}v1/address_validator`
    const data = await requestJson(url, requestOptions)
    return Array.isArray(data) ? data : []
}

export const newAgreement = async (esiid, meterNumber, certificateNumber, email, forwardEmail) => {
    const myHeaders = new Headers()
    myHeaders.append('Authorization', `Bearer ${ACC_KEY}`)

    const formdata = new FormData()
    formdata.append('esiid', esiid)
    formdata.append('meter_number', meterNumber)
    formdata.append('certificate_number', certificateNumber)
    formdata.append('email', email)
    formdata.append('email_forward', forwardEmail)

    const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: formdata,
        redirect: 'follow',
    }

    const url = `${API_URL}v2/smt/new_agreement`
    return requestJson(url, requestOptions)
}

export const getAgreement = async (consentId) => {
    const myHeaders = new Headers()
    myHeaders.append('Authorization', `Bearer ${ACC_KEY}`)

    const formdata = new FormData()
    formdata.append('consent_id', consentId)
    formdata.append('vip', '1')

    const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: formdata,
        redirect: 'follow',
    }

    const url = `${API_URL}v2/smt/get_agreement`
    return requestJson(url, requestOptions)
}

export const fetchRetailElectricProviders = async () => {
    const myHeaders = new Headers()
    myHeaders.append('Authorization', `Bearer ${ACC_KEY}`)

    const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        redirect: 'follow',
    }

    const url = `${API_URL}v2/smt/retail_electric_providers`
    const data = await requestJson(url, requestOptions)
    return Array.isArray(data) ? data : []
}

export const fetchUtility = async (zipcode) => {
    const myHeaders = new Headers()
    myHeaders.append('Authorization', `Bearer ${ACC_KEY}`)

    const formdata = new FormData()
    formdata.append('zipcode', zipcode)

    const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: formdata,
        redirect: 'follow',
    }

    const url = `${API_URL}v2/utility/get`
    const data = await requestJson(url, requestOptions)
    return data && String(data.status) === '1' ? data.response : []
}

/*
 * Usage Estimator
 * Use cases:        https://www.powerlego.com/cases/usage_estimator
 * Postman examples: https://documenter.getpostman.com/view/4831254/SzKYPH3c#bd865131-a3b9-4b62-8254-8abc47380670
 */
export const fetchEstUsage = async (address, city, state, zipcode) => {
    const myHeaders = new Headers()
    myHeaders.append('Authorization', `Bearer ${ACC_KEY}`)

    const formdata = new FormData()
    formdata.append('address1', address)
    formdata.append('city', city)
    formdata.append('state', state)
    formdata.append('zipcode', zipcode)

    const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: formdata,
        redirect: 'follow',
    }

    const url = `${API_URL}v1/usage_estimator`
    return requestJson(url, requestOptions)
}

export const fetchIntervalUsage = async (esiid, consentId, startDate, endDate) => {
    const myHeaders = new Headers()
    myHeaders.append('Authorization', `Bearer ${ACC_KEY}`)

    const formdata = new FormData()
    formdata.append('consent_id', consentId)
    formdata.append('esiid', esiid)
    formdata.append('start_date', startDate)
    formdata.append('end_date', endDate)

    const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: formdata,
        redirect: 'follow',
    }

    const url = `${API_URL}v2/smt/interval15`
    return requestJson(url, requestOptions)
}

export const fetchMonthlyUsage = async (esiid, consentId, startDate, endDate) => {
    const myHeaders = new Headers()
    myHeaders.append('Authorization', `Bearer ${ACC_KEY}`)

    const formdata = new FormData()
    formdata.append('consent_id', consentId)
    formdata.append('esiid', esiid)
    formdata.append('start_date', startDate)
    formdata.append('end_date', endDate)

    const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: formdata,
        redirect: 'follow',
    }

    const url = `${API_URL}v2/smt/monthly`
    return requestJson(url, requestOptions)
}

export const fetchDailyUsage = async (esiid, consentId, startDate, endDate) => {
    const myHeaders = new Headers()
    myHeaders.append('Authorization', `Bearer ${ACC_KEY}`)

    const formdata = new FormData()
    formdata.append('consent_id', consentId)
    formdata.append('esiid', esiid)
    formdata.append('start_date', startDate)
    formdata.append('end_date', endDate)

    const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: formdata,
        redirect: 'follow',
    }

    const url = `${API_URL}v2/smt/daily`
    return requestJson(url, requestOptions)
}

const getPlanFormData = (type, usage, zipcode, filter, utilityCode) => {
    const formdata = new FormData()
    formdata.append('zipcode', zipcode)
    formdata.append('utility_code', utilityCode)

    if (usage) {
        if (type === 'normal') {
            for (let u in usage) {
                formdata.append(`usage[monthly][${u}]`, usage[u])
            }
            formdata.append('filter[type]', 'normal')
        } else {
            for (let u in usage) {
                const data = usage[u]
                for (let i in data) {
                    if (i === 'free') continue
                    formdata.append(`usage[monthly][${u}]`, usage[u][i])
                }
            }
            formdata.append('filter[type]', `free_${type}`)
        }
    } else {
        if (type === 'normal') {
            formdata.append('filter[type]', 'normal')
        } else {
            formdata.append('filter[type]', 'tou')
        }
    }

    for (let f in filter) {
        if (f === 'provider_id') {
            filter.provider_id.forEach((val, idx) => {
                formdata.append(`filter[provider_id][${idx}]`, val)
            })
        } else if (f === 'term') {
            filter.term.forEach((val, idx) => {
                if (!val) return true
                formdata.append(`filter[term][${idx}]`, val)
            })
        } else if (f === 'prepaid') {
            if (filter.prepaid !== 'all') {
                formdata.append(`filter[prepaid]`, filter.prepaid === 'true')
            }
        } else {
            if (f !== 'type') {
                if (filter[f]) formdata.append(`filter[${f}]`, filter[f])
            }
        }
    }
    return formdata
}

export const fetchPlans = async (type, usage, zipcode, filter, utilityCode) => {
    const myHeaders = new Headers()
    myHeaders.append('Authorization', `Bearer ${ACC_KEY}`)

    const formdata = getPlanFormData(type, usage, zipcode, filter, utilityCode)

    const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: formdata,
        redirect: 'follow',
    }

    const url = `${API_URL}v2/texasEnergyGroup/plan`
    return requestJson(url, requestOptions)
}

export const fetchFilterOptions = async (type, usage, zipcode, filter, utilityCode) => {
    const myHeaders = new Headers()
    myHeaders.append('Authorization', `Bearer ${ACC_KEY}`)

    const formdata = getPlanFormData(type, usage, zipcode, filter, utilityCode)

    const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: formdata,
        redirect: 'follow',
    }

    const url = `${API_URL}v2/texasEnergyGroup/filter`
    return requestJson(url, requestOptions)
}

export const fetchPremiseDetail = async (zipcode, address) => {
    const myHeaders = new Headers()
    myHeaders.append('Authorization', `Bearer ${ACC_KEY}`)

    const formdata = new FormData()
    formdata.append('zipcode', zipcode)
    formdata.append('address', address)

    const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: formdata,
        redirect: 'follow',
    }

    const url = `${API_URL}v2/premise/detail`
    return requestJson(url, requestOptions)
}

export const billReader = async (file) => {
    const formData = new FormData()
    formData.append('file', file)
    return requestJson(`${API_URL}v1/bill_reader`, {
        method: 'POST',
        headers: {
            accKey: ACC_KEY,
        },
        body: formData,
    })
}

export const fetchRepList = async () => {
    const myHeaders = new Headers()
    myHeaders.append('Authorization', `Bearer ${ACC_KEY}`)

    const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        redirect: 'follow',
    }

    const url = `${API_URL}v1/rep_list`
    const data = await requestJson(url, requestOptions)
    return Array.isArray(data) ? data : []
}

export const getMyAccountInfo = async (username, password, rep) => {
    const myHeaders = new Headers()

    myHeaders.append('Authorization', `Bearer ${ACC_KEY}`)

    const formdata = new FormData()
    formdata.append('username', username)
    formdata.append('password', password)
    formdata.append('rep', rep)
    formdata.append('method', '0')

    const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: formdata,
        redirect: 'follow',
    }

    const url = `${API_URL}v1/rep_usage`
    return requestJson(url, requestOptions)
}

export const getPlanAvgPrice = async (address, zipcode) => {
    const myHeaders = new Headers()
    myHeaders.append('Authorization', `Bearer ${ACC_KEY}`)

    const formdata = new FormData()
    formdata.append('address', address)
    formdata.append('zipcode', zipcode)
    const url = `${SIDEBYSIDE_API_URL}plan/average_price`

    return requestJson(url, {
        method: 'POST',
        headers: myHeaders,
        body: formdata,
        redirect: 'follow',
    })
}

export const fetchSidebySidePlans = async (
    planId,
    rate,
    zipcode,
    utilityCode,
    usages,
    planType,
    terms
) => {
    const requestData = `plan_id=${planId}&rate=${rate}&zipcode=${zipcode}&utility_code=${utilityCode}&filter[type]=${planType}&website=TEG&website_env=PROD`
    const usageData = []
    const termsData = []

    for (let i in usages) {
        usageData.push(`usage[monthly][${i}]=${usages[i]}`)
    }

    for (let j in terms) {
        if (terms[j]) {
            termsData.push(`term[${j}]=${terms[j]}`)
        }
    }

    return requestJson(
        'https://bb0xg7o51l.execute-api.us-east-2.amazonaws.com/beta/ComparePrices/index',
        {
            method: 'POST',
            headers: {
                'content-type': 'application/x-www-form-urlencoded',
                authorization: `Bearer ${ACC_KEY}`,
            },
            body: `${requestData}&${usageData.join('&')}${
                termsData.length > 0 ? `&${termsData.join('&')}` : ''
            }`,
        }
    )
}

export const submitContactForm = async ({ name, phone, email, message }) => {
    const myHeaders = new Headers()
    myHeaders.append('Authorization', `Bearer ${ACC_KEY}`)

    const formdata = new FormData()
    formdata.append('name', name)
    formdata.append('phone', phone)
    formdata.append('email', email)
    formdata.append('message', message)

    const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: formdata,
        redirect: 'follow',
    }

    const url = `${API_URL}v2/texasEnergyGroup/contact`
    return requestJson(url, requestOptions)
}

export const fetchReportProspect = async (email, date) => {
    const myHeaders = new Headers()
    myHeaders.append('Authorization', `Bearer ${ACC_KEY}`)

    const formdata = new FormData()
    formdata.append('customer_email', email)
    formdata.append('expiring_date', date)

    const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: formdata,
        redirect: 'follow',
    }

    const url = `${API_URL}v2/contact/prospect`

    return requestJson(url, requestOptions)
}
